import * as XLSX from 'xlsx';
import { normalizeTipoCuentaCodigo, normalizeVisibleEnOption } from '../services/imputableService';

export type PlanCuentasDestino = 'imputable' | 'no_imputable';
export type PlanCuentasEstado = 'Nuevo' | 'Existente' | 'Error';
export type PlanCuentasAccion = 'Agregar' | 'Actualizar' | 'Omitir';

export interface PlanCuentasImportFila {
  fila: number;
  codigo: string;
  nombre: string;
  nroRubro: number;
  imputableFlag: boolean;
  destino: PlanCuentasDestino;
  tipo: string;
  visibleEn: string;
  nivel: number;
  totalizaEn?: number;
  descripcion?: string;
}

export interface PlanCuentasPreviewFila extends PlanCuentasImportFila {
  estado: PlanCuentasEstado;
  mensaje?: string;
  accion: PlanCuentasAccion;
}

export interface PlanCuentasImportParseError {
  fila: number;
  mensaje: string;
}

export interface PlanCuentasImportParseResult {
  filas: PlanCuentasImportFila[];
  errores: PlanCuentasImportParseError[];
}

export const PLAN_CUENTAS_EXPORT_COLUMNS = [
  'Código',
  'Nombre',
  'Nro. rubro',
  'No imputable',
  'Imputable',
  'Tipo de cuenta',
  'Visible en',
  'Nivel',
  'Totaliza en',
] as const;

const cellText = (value: unknown): string => {
  if (value == null) return '';
  return String(value).trim();
};

const parseNumber = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const n = Number(String(value).trim().replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const isImputableSi = (value: unknown): boolean | null => {
  const s = cellText(value).toLowerCase();
  if (!s) return null;
  if (['sí', 'si', 's', 'yes', 'y', 'true', '1'].includes(s)) return true;
  if (['no', 'n', 'false', '0'].includes(s)) return false;
  return null;
};

const getCell = (row: Record<string, unknown>, ...keys: string[]): unknown => {
  for (const key of keys) {
    if (key in row && row[key] != null && String(row[key]).trim() !== '') {
      return row[key];
    }
  }
  const entries = Object.entries(row);
  for (const key of keys) {
    const want = key.toLowerCase();
    const found = entries.find(([k]) => k.trim().toLowerCase() === want);
    if (found && found[1] != null && String(found[1]).trim() !== '') return found[1];
  }
  return undefined;
};

export function parsePlanCuentasExcelRows(
  data: Record<string, unknown>[]
): PlanCuentasImportParseResult {
  const filas: PlanCuentasImportFila[] = [];
  const errores: PlanCuentasImportParseError[] = [];

  if (!data.length) {
    return { filas: [], errores: [{ fila: 0, mensaje: 'No hay datos para importar.' }] };
  }

  data.forEach((row, index) => {
    const fila = index + 2;
    const codigo = cellText(getCell(row, 'Código', 'Codigo', 'código'));
    const nombre = cellText(getCell(row, 'Nombre'));
    const imputableRaw = getCell(row, 'Imputable');
    const flag = isImputableSi(imputableRaw);

    if (!codigo && !nombre && imputableRaw == null) return;

    if (!codigo || !Number.isFinite(Number(codigo))) {
      errores.push({ fila, mensaje: 'Código vacío o no numérico.' });
      return;
    }
    if (flag == null) {
      errores.push({
        fila,
        mensaje: 'Columna Imputable inválida (use Sí o No).',
      });
      return;
    }
    if (!nombre) {
      errores.push({ fila, mensaje: 'Nombre obligatorio.' });
      return;
    }

    const nroRubroRaw = getCell(row, 'Nro. rubro', 'NroRubro', 'Nro rubro');
    const nroRubro = parseNumber(nroRubroRaw) ?? 0;
    const nivel = parseNumber(getCell(row, 'Nivel', 'Segmento')) ?? 0;
    const totalizaEnRaw = parseNumber(getCell(row, 'Totaliza en', 'TotalizaEn', 'Totaliza'));
    const tipoRaw = getCell(row, 'Tipo de cuenta', 'Tipo');
    const visibleRaw = getCell(row, 'Visible en', 'Visible en...');
    const descripcion = cellText(getCell(row, 'Descripción', 'Descripcion')) || undefined;

    filas.push({
      fila,
      codigo,
      nombre,
      nroRubro,
      imputableFlag: flag,
      destino: flag ? 'imputable' : 'no_imputable',
      tipo: normalizeTipoCuentaCodigo(
        tipoRaw != null && tipoRaw !== '' ? String(tipoRaw) : undefined
      ),
      visibleEn: normalizeVisibleEnOption(
        visibleRaw != null ? String(visibleRaw) : undefined
      ),
      nivel,
      totalizaEn:
        totalizaEnRaw != null && totalizaEnRaw > 0 ? totalizaEnRaw : undefined,
      descripcion,
    });
  });

  return { filas, errores };
}

export async function parsePlanCuentasExcelFile(
  file: File
): Promise<PlanCuentasImportParseResult> {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array' });
  const wsname = wb.SheetNames[0];
  if (!wsname) {
    return { filas: [], errores: [{ fila: 0, mensaje: 'El archivo no tiene hojas.' }] };
  }
  const ws = wb.Sheets[wsname];
  const data = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' });
  return parsePlanCuentasExcelRows(data);
}

export function buildPlanCuentasPreview(
  filas: PlanCuentasImportFila[],
  existentesImputables: Set<string>,
  existentesNoImputables: Set<number>
): PlanCuentasPreviewFila[] {
  return filas.map((f) => {
    if (f.destino === 'imputable') {
      const existe = existentesImputables.has(f.codigo);
      return {
        ...f,
        estado: existe ? 'Existente' : 'Nuevo',
        accion: existe ? 'Omitir' : 'Agregar',
        mensaje: existe
          ? 'Ya existe como imputable'
          : 'Se creará como imputable',
      };
    }

    const codigoNum = Number(f.codigo);
    const existe = existentesNoImputables.has(codigoNum);
    return {
      ...f,
      estado: existe ? 'Existente' : 'Nuevo',
      accion: existe ? 'Omitir' : 'Agregar',
      mensaje: existe
        ? 'Ya existe como no imputable (rubro)'
        : 'Se creará como no imputable (rubro)',
    };
  });
}
